import { useEffect, useRef, useState } from "react";
import { useFetcher } from "react-router";
import type { FavoriteItem } from "~/contexts/favorites.context";

type UseSyncFavoritesProps = {
	isAuthenticated: boolean;
	favoriteItems: FavoriteItem[];
	setFavoriteItems: (
		value: FavoriteItem[] | ((prev: FavoriteItem[]) => FavoriteItem[]),
	) => void;
	isClient: boolean;
	hasPendingOperations?: boolean;
	shouldSyncFavoritesAfterLogin?: boolean;
	shouldPullServerFavorites?: boolean;
};

export function useSyncFavorites({
	isAuthenticated,
	favoriteItems,
	setFavoriteItems,
	isClient,
	hasPendingOperations = false,
	shouldSyncFavoritesAfterLogin = false,
	shouldPullServerFavorites = false,
}: UseSyncFavoritesProps) {
	const syncFetcher = useFetcher<{
		favorites: Array<{ id: string | number }>;
	}>();
	const pullFetcher = useFetcher<{
		favorites: Array<{ id: string | number }>;
	}>();
	const persistFetcher = useFetcher();
	const syncedIdsRef = useRef<number[]>([]);
	const [hasSynced, setHasSynced] = useState(false);
	const [hasPulled, setHasPulled] = useState(false);
	const favoriteItemsRef = useRef(favoriteItems);

	useEffect(() => {
		favoriteItemsRef.current = favoriteItems;
	}, [favoriteItems]);

	// 0. Pull server favorites on /favorites (authenticated) to reflect server-side deletes in localStorage.
	// This is a pure pull/overwrite and does NOT push local state back to the server.
	useEffect(() => {
		if (
			isClient &&
			isAuthenticated &&
			shouldPullServerFavorites &&
			!hasPulled &&
			!hasPendingOperations
		) {
			const params = new URLSearchParams();
			params.set("productIds", JSON.stringify([]));
			pullFetcher.load(`/api/favorites?${params.toString()}`);
			setHasPulled(true);
		}
	}, [
		isClient,
		isAuthenticated,
		shouldPullServerFavorites,
		hasPulled,
		hasPendingOperations,
		pullFetcher,
	]);

	useEffect(() => {
		if (!isAuthenticated || !shouldPullServerFavorites) return;
		if (!pullFetcher.data?.favorites) return;

		const serverFavoriteIds = pullFetcher.data.favorites.map((fav) =>
			Number(fav.id),
		);
		const localCreatedAtById = new Map(
			favoriteItemsRef.current.map((item) => [item.productId, item.createdAt]),
		);
		const nowIso = new Date().toISOString();
		setFavoriteItems(
			serverFavoriteIds.map((id) => ({
				productId: id,
				createdAt: localCreatedAtById.get(id) ?? nowIso,
			})),
		);
	}, [
		isAuthenticated,
		shouldPullServerFavorites,
		pullFetcher.data,
		setFavoriteItems,
	]);

	// 1. Initial Fetch: Load server favorites only once per session/page load.
	// This ensures we get the "truth" from the server to merge with local data.
	useEffect(() => {
		if (
			isClient &&
			isAuthenticated &&
			!hasSynced &&
			shouldSyncFavoritesAfterLogin
		) {
			const params = new URLSearchParams();
			params.set("productIds", JSON.stringify([]));
			syncFetcher.load(`/api/favorites?${params.toString()}`);
			setHasSynced(true);
		}
	}, [
		isClient,
		isAuthenticated,
		hasSynced,
		shouldSyncFavoritesAfterLogin,
		syncFetcher,
	]);

	// 2. Synchronization Logic (One-time Merge & Push)
	// Runs ONLY when initial server data arrives.
	// We do NOT depend on 'favoriteItems' here to prevent re-running on every local user action (add/remove),
	// which avoids race conditions and "zombie" items reappearing after deletion.
	useEffect(() => {
		if (
			!isAuthenticated ||
			!shouldSyncFavoritesAfterLogin ||
			!syncFetcher.data?.favorites
		)
			return;

		const serverFavoriteIds = syncFetcher.data.favorites.map((fav) =>
			Number(fav.id),
		);

		// A. Server -> Local (Merge)
		// Add items present on server but missing locally.
		setFavoriteItems((currentItems) => {
			const localFavoriteIds = currentItems.map((item) => item.productId);

			const newServerItems = serverFavoriteIds.filter(
				(serverId) => !localFavoriteIds.includes(serverId),
			);

			if (newServerItems.length === 0) return currentItems;

			const newFavoriteItems: FavoriteItem[] = newServerItems.map((id) => ({
				productId: id,
				createdAt: new Date().toISOString(),
			}));

			return [...currentItems, ...newFavoriteItems];
		});

		// B. Local -> Server (Push)
		// Send items present locally (e.g. from guest session) but missing on server.
		// Uses ref to get current state without triggering re-render loop.
		const currentLocalItems = favoriteItemsRef.current;
		const localFavoriteIds = currentLocalItems.map((item) => item.productId);

		const newIdsToSync = localFavoriteIds.filter(
			(id) =>
				!serverFavoriteIds.includes(id) && !syncedIdsRef.current.includes(id),
		);

		if (newIdsToSync.length > 0 && persistFetcher.state === "idle") {
			syncedIdsRef.current = [...syncedIdsRef.current, ...newIdsToSync];

			persistFetcher.submit(
				{ productIds: newIdsToSync },
				{
					method: "POST",
					action: "/api/favorites/sync",
					encType: "application/json",
				},
			);
		}
	}, [
		syncFetcher.data,
		setFavoriteItems,
		isAuthenticated,
		shouldSyncFavoritesAfterLogin,
		persistFetcher,
	]);
}
